/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TCopilot } from "../types/copilot";
import { Reference } from "../types/reference";
import { CodeableConcept } from "../types/codeableConcept";
import { medicalsApi } from "../api/medicalsApi";
import {
  MedicalEncounter,
  MedicalEncounterStatus,
} from "../types/medicalEncounter";
import {
  NotificationVariant,
  useNotifications,
} from "../components/dashboard/NotificationsProvider";
import useRoles from "../hooks/useRoles";
import { EncounterTemplate } from "../types/templates";
import templateApi from "../api/medicals/templateApi";
import useTemplates from "../pages/facility/hooks/useTemplates";
import { QueryKeys } from "../constants/queryKeys";

export type TMedicalEncounter = {
  isCopiloting: boolean;
  currentName: any;
  currentPatient: any;
  currentEncounter: any;
  currentItem: any;
  encounterReference: Reference;
  patientReference: Reference;
  currentDiagnosis: CodeableConcept[];
  refreshHistory;
  refreshProcedure;
  refetchCurrentDiagnosis;
  copilot?: TCopilot;
  setCurrentPatient: (e: any) => void;
  setCurrentItem: (e: any) => void;
  setCurrentEncounter: (e: any) => void;
  setEncounterToInProgress: () => Promise<void>;
  resetEncounter: () => void;
  setCurrentEncounterRefrence: (e: any) => void;
  setCurrentPatientReference: (e: any) => void;
  setEncounterDiagnosis: (e: CodeableConcept[]) => void;
  setCurrentName: (e: any) => void;
  refreshPatientHistory: () => void;
  refreshProceduresList: () => void;
  refreshDiagnosisList: () => void;
  setIsCopiloting: (e) => void;
  setCopilot: (e) => void;
  updateEncounter: (e: Partial<MedicalEncounter>) => Promise<void>;
  currentTemplate: EncounterTemplate | undefined;
  isLoadingTemplate: boolean;
  isProcessingTranscript: boolean;
  setIsProcessingTranscript: (processing: boolean) => void;
  /** In-progress speech from Deepgram streaming (interim + speech_final display) */
  liveTranscript: string;
  setLiveTranscript: (text: string) => void;
};

const defaultApi: TMedicalEncounter = {
  isCopiloting: false,
  currentName: undefined,
  currentPatient: undefined,
  currentEncounter: undefined,
  currentItem: undefined,
  encounterReference: undefined,
  patientReference: undefined,
  currentDiagnosis: undefined,
  refreshHistory: undefined,
  refreshProcedure: undefined,
  refetchCurrentDiagnosis: undefined,
  copilot: undefined,
  setCurrentPatient: (e): void => null,
  setCurrentItem: (e): void => null,
  setCurrentEncounter: (e): void => null,
  setEncounterToInProgress: (): Promise<void> => null,
  resetEncounter: (): void => null,
  setCurrentEncounterRefrence: (e): void => null,
  setCurrentPatientReference: (e): void => null,
  setEncounterDiagnosis: (e): void => null,
  setCurrentName: (e): void => null,
  refreshPatientHistory: (): void => null,
  refreshProceduresList: (): void => null,
  refreshDiagnosisList: (): void => null,
  setIsCopiloting: (e): void => null,
  setCopilot: (e): void => null,
  updateEncounter: (e: Partial<MedicalEncounter>) => null,
  currentTemplate: undefined,
  isLoadingTemplate: false,
  isProcessingTranscript: false,
  setIsProcessingTranscript: (processing: boolean): void => null,
  liveTranscript: "",
  setLiveTranscript: (text: string): void => null,
};

export const MedicalEncounterContext = createContext(defaultApi);

export function MedicalEncounterProvider({ children }: any) {
  const userRoles = useRoles();
  const [currentPatient, setPatient] = useState<any>(undefined);
  const [currentEncounter, setEncounter] = useState<any>(undefined);
  const [currentItem, setItem] = useState<any>(undefined);
  const [refreshHistory, setRefreshHistory] = useState(0);
  const [refreshProcedure, setRefreshProcedure] = useState<boolean>(false);
  const [encounterReference, setEncounterReference] = useState<any>(undefined);
  const [patientReference, setPatientReference] = useState<any>(undefined);
  const [currentDiagnosis, setCurrentDiagnosis] = useState<CodeableConcept[]>(
    [],
  );
  const [refetchCurrentDiagnosis, setRefetchCurrentDiagnosis] =
    useState<boolean>(false);
  const [currentName, setCurrentFirstName] = useState<any>(undefined);

  const [isCopiloting, setIsCopiloting] = useState<boolean>(false);
  const [copilot, setCopilot] = useState<TCopilot>();
  const [isProcessingTranscript, setIsProcessingTranscript] =
    useState<boolean>(false);
  const [liveTranscript, setLiveTranscript] = useState<string>("");
  const { setNotification } = useNotifications();

  const setCurrentName = (name) => setCurrentFirstName(name);
  const setCurrentPatient = (patient) => setPatient(patient);
  const setCurrentEncounter = (encounter) => setEncounter(encounter);
  const setCurrentItem = (item) => setItem(item);
  const setCurrentPatientReference = (patient) => setPatientReference(patient);
  const setCurrentEncounterRefrence = (encounter) =>
    setEncounterReference(encounter);
  const setEncounterDiagnosis = (diagnosisList: CodeableConcept[]) =>
    setCurrentDiagnosis(diagnosisList);
  const [currentTemplate, setCurrentTemplate] = useState<any>(undefined);
  const { detailedTemplates, soapTemplates } = useTemplates();
  const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const refreshPatientHistory = () => {
    setRefreshHistory((count) => count + 1);
  };

  const refreshProceduresList = () => {
    setRefreshProcedure(true);
    setRefreshProcedure(false);
  };

  const refreshDiagnosisList = () => {
    setRefetchCurrentDiagnosis(true);
    setRefetchCurrentDiagnosis(false);
  };

  const resetEncounter = () => {
    setEncounter(undefined);
    setPatient(undefined);
    setPatientReference(undefined);
    setEncounterReference(undefined);
    setEncounterDiagnosis(undefined);
  };

  // allows for partial update of encounter
  const updateEncounter = useCallback(
    async (updateData: Partial<MedicalEncounter>, errorMessage?: string) => {
      const encounterId = currentEncounter?.id;
      if (!encounterId) {
        return;
      }

      try {
        const response = await medicalsApi.updateEncounter(encounterId, {
          id: encounterId,
          ...updateData,
        });

        setEncounter(response);

        queryClient.invalidateQueries({
          queryKey: [QueryKeys.ENCOUNTERS],
        });
      } catch (error) {
        console.error(error);
        setNotification({
          message:
            errorMessage ||
            "Unable to update medication encounter. Please refresh the page and try again, or contact technical support.",
          variant: NotificationVariant.error,
        });
      }
    },
    [currentEncounter, setNotification],
  );

  const setEncounterToInProgress = useCallback(async () => {
    const status: MedicalEncounterStatus = "in-progress";
    if (
      ["planned", "arrived", "triaged", "on-leave"].includes(
        currentEncounter?.status,
      ) &&
      userRoles.includes("Practitioner")
    ) {
      await updateEncounter(
        {
          status,
        },
        "A system error prevented the encounter status from changing to ‘In Progress’. Please update it manually in the encounter header, or contact technical support for assistance.",
      );
    }
  }, [currentEncounter, updateEncounter, userRoles]);

  const getTemplate = useCallback(
    async (id: string, version: number) => {
      try {
        setIsLoadingTemplate(true);
        const template = await templateApi.getTemplateByVersion(id, version);
        setCurrentTemplate(template);
      } catch (error) {
        console.error(error);
        setNotification({
          message:
            "Unable to get template set on Encounter. Please refresh the page and try again, or contact technical support.",
          variant: NotificationVariant.error,
        });
      } finally {
        setIsLoadingTemplate(false);
      }
    },
    [setNotification, setIsLoadingTemplate],
  );

  useEffect(() => {
    const encounterTemplateRef = currentEncounter?.templatingRef;

    if (!currentEncounter || !encounterTemplateRef) {
      if (currentTemplate) {
        setCurrentTemplate(undefined);
      }
      return;
    }

    // get template if id and version are different
    if (
      currentTemplate?.id !== encounterTemplateRef?.id ||
      currentTemplate?.meta.versionId !== encounterTemplateRef?.v
    ) {
      const savedTemplate =
        detailedTemplates.find((t) => t.id === encounterTemplateRef.id) ||
        soapTemplates.find((t) => t.id === encounterTemplateRef.id);
      if (savedTemplate?.meta.versionId === encounterTemplateRef.v) {
        setCurrentTemplate(savedTemplate);
        return;
      }
      getTemplate(encounterTemplateRef.id, encounterTemplateRef.v);
    }
  }, [
    currentEncounter,
    currentTemplate,
    getTemplate,
    detailedTemplates,
    soapTemplates,
  ]);

  // Return Provider with full API
  const api = {
    isCopiloting,
    currentName,
    currentDiagnosis,
    currentPatient,
    currentEncounter,
    currentItem,
    encounterReference,
    patientReference,
    refreshHistory,
    refreshProcedure,
    refetchCurrentDiagnosis,
    setCurrentItem,
    setCurrentPatient,
    setCurrentEncounter,
    resetEncounter,
    setCurrentEncounterRefrence,
    setCurrentPatientReference,
    setEncounterDiagnosis,
    setEncounterToInProgress,
    setCurrentName,
    refreshPatientHistory,
    refreshProceduresList,
    refreshDiagnosisList,
    setIsCopiloting,
    copilot,
    setCopilot,
    updateEncounter,
    currentTemplate,
    isLoadingTemplate,
    isProcessingTranscript,
    setIsProcessingTranscript,
    liveTranscript,
    setLiveTranscript,
  };

  return (
    <MedicalEncounterContext.Provider value={api}>
      {children}
    </MedicalEncounterContext.Provider>
  );
}
